import React, { useState, useEffect } from 'react';
import { makeStyles } from "@material-ui/core/styles";
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import FlagstoneTheme from '../../../Config/flagstoneTheme';
import { useTranslation } from 'react-i18next';
import TextField from '@material-ui/core/TextField';
import InputLabel from '@material-ui/core/InputLabel';
import CircularProgress from '@material-ui/core/CircularProgress';
import { dataService } from '../../../Services/data.service';
import { toast } from 'react-toastify';

export default function AddCommentDialog(props) {

    const classes = useStyles();
    const { t } = useTranslation();
    const [comment, setComment] = useState('');
    const [existingComments, setExistingComments] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (props.dialogOpen && props.orderLineId) {
            fetchComments()
        }
    }, [props.dialogOpen, props.orderLineId]);

    const fetchComments = async () => {
        setLoading(true);
        try {
            const comments = await dataService.getOperatorComments(props.orderLineId);
            setExistingComments(comments || []);
        } catch (error) {
            console.error('Error fetching comments:', error);
            toast.error(t('Fout bij ophalen notities'), { position: "top-center" });
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = () => {
        props.onSubmit(comment);
        setComment('');
        // Refresh comments after a short delay to allow backend to process
        setTimeout(() => {
            if (props.orderLineId) {
                fetchComments();
            }
        }, 500);
    };

    const handleCancel = () => {
        setComment('');
        props.closeDialog();
    };

    const formatDateTime = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleString('nl-BE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Dialog 
            className={classes.dialogContainer}
            open={props.dialogOpen}
            onClose={handleCancel}
            aria-labelledby="dialog-title"
            maxWidth="md"
            fullWidth
        >
            <DialogTitle className={classes.dialogTitle} id="dialog-title">
                {t("Notitie toevoegen")}
                <IconButton
                    aria-label="close"
                    className={classes.closeButton}
                    onClick={handleCancel}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
                {/* Existing Comments Section */}
                {existingComments.length > 0 && (
                    <div className={classes.existingCommentsSection}>
                        <InputLabel className={classes.label}>
                            {t("Eerdere notities")} ({existingComments.length})
                        </InputLabel>
                        <div className={classes.commentsListContainer}>
                            {loading ? (
                                <div className={classes.loadingContainer}>
                                    <CircularProgress size={30} />
                                </div>
                            ) : (
                                existingComments.map((existingComment, index) => (
                                    <div key={index} className={classes.commentItem}>
                                        <div className={classes.commentHeader}>
                                            <span className={classes.commentTimestamp}>
                                                {formatDateTime(existingComment.timestamp)}
                                            </span>
                                        </div>
                                        <div className={classes.commentText}>
                                            {existingComment.comment}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}

                {/* New Comment Section */}
                <div className={classes.newCommentSection}>
                    <InputLabel className={classes.label}>
                        {t("Nieuwe notitie")}
                    </InputLabel>
                    <TextField
                        className={classes.textField}
                        multiline
                        rows={6}
                        variant="outlined"
                        fullWidth
                        placeholder={t("Schrijf hier wat je wilt toevoegen aan de werklijn")}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        autoFocus
                    />
                </div>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                <Button 
                    className={classes.cancelButton} 
                    disableRipple 
                    onClick={handleCancel}
                >
                    {t("Annuleren")}
                </Button>
                <Button 
                    className={classes.submitButton} 
                    disableRipple 
                    onClick={handleSubmit}
                    disabled={!comment.trim()}
                >
                    {t("Notitie opslaan")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

const useStyles = makeStyles(() => ({
    dialogContainer: {
        '& .MuiDialog-paper': {
            width: "700px",
            maxWidth: "90vw",
            maxHeight: "85vh",
            padding: "16px"
        }
    },
    dialogTitle: {
        color: "#000000",
        fontFamily: "Lato, sans-serif",
        fontWeight: 'medium',
        textAlign: "left",
        '& h2': {
            fontSize: "24px !important",
        },
        padding: "0",
        paddingBottom: "16px",
        position: "relative"
    },
    closeButton: {
        position: "absolute",
        right: "0",
        top: "0",
        color: "#000000"
    },
    dialogContent: {
        padding: "0",
        paddingBottom: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        overflowY: "auto",
        maxHeight: "calc(85vh - 150px)"
    },
    existingCommentsSection: {
        display: "flex",
        flexDirection: "column",
        gap: "8px"
    },
    newCommentSection: {
        display: "flex",
        flexDirection: "column",
        gap: "4px"
    },
    label: {
        fontSize: "14px",
        color: "#0C0E29",
        fontFamily: "Lato, sans-serif",
        fontWeight: "600",
        display: "block"
    },
    commentsListContainer: {
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        maxHeight: "300px",
        overflowY: "auto",
        padding: "12px",
        backgroundColor: "#f9f9f9",
        borderRadius: "8px",
        border: "1px solid #e0e0e0"
    },
    commentItem: {
        backgroundColor: "#ffffff",
        padding: "12px",
        borderRadius: "6px",
        border: "1px solid #e0e0e0",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
    },
    commentHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "8px",
        paddingBottom: "8px",
        borderBottom: "1px solid #f0f0f0"
    },
    commentOperator: {
        fontSize: "13px",
        fontFamily: "Lato, sans-serif",
        fontWeight: "600",
        color: FlagstoneTheme.palette.primary.main
    },
    commentTimestamp: {
        fontSize: "12px",
        fontFamily: "Lato, sans-serif",
        color: "#666666"
    },
    commentText: {
        fontSize: "14px",
        fontFamily: "Lato, sans-serif",
        color: "#333333",
        lineHeight: "1.5",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word"
    },
    loadingContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "20px"
    },
    textField: {
        '& .MuiOutlinedInput-root': {
            fontFamily: "Lato, sans-serif",
            fontSize: "16px",
            padding: "12px",
            '& fieldset': {
                borderColor: '#ddd',
                borderWidth: '1px',
            },
            '&:hover fieldset': {
                borderColor: FlagstoneTheme.palette.primary.main,
                borderWidth: '1px',
            },
            '&.Mui-focused fieldset': {
                borderColor: FlagstoneTheme.palette.primary.main,
                borderWidth: '1px',
            },
        },
        '& .MuiOutlinedInput-input': {
            padding: "0px",
        },
        '& .MuiInputBase-inputMultiline': {
            padding: "0px !important",
        }
    },
    dialogActions: {
        justifyContent: "space-between",
        padding: "0",
    },
    cancelButton: {
        color: FlagstoneTheme.palette.primary.main,
        fontSize: "16px",
        fontFamily: "Lato, sans-serif",
        fontWeight: 'medium',
        backgroundColor: "white",
        border: `1px solid ${FlagstoneTheme.palette.primary.main}`,
        '&:hover': {
            backgroundColor: "#f5f5f5",
        },
        padding: "8px 20px",
        textTransform: "none",
    },
    submitButton: {
        color: "white",
        fontSize: "16px",
        fontFamily: "Lato, sans-serif",
        fontWeight: 'medium',
        backgroundColor: FlagstoneTheme.palette.primary.main,
        '&:hover': {
            backgroundColor: FlagstoneTheme.palette.primary.main,
            opacity: 0.9,
        },
        '&:disabled': {
            backgroundColor: "#cccccc",
            color: "#888888"
        },
        padding: "8px 20px",
        textTransform: "none",
    }
}));
